import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { workingTasksAPI } from '../services/api';
import '../styles/Focus.css';

const Focus = () => {
    const [workingTasks, setWorkingTasks] = useState([]);
    const [currentTask, setCurrentTask] = useState(null);
    const [timer, setTimer] = useState(0); // This will count DOWN from expected duration
    const [stepTimer, setStepTimer] = useState(0); // Timer for current step
    const [isRunning, setIsRunning] = useState(false);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        loadWorkingTasks();
    }, []);

    // Save timer state to localStorage
    useEffect(() => {
        if (currentTask && isRunning) {
            const timerState = {
                taskId: currentTask.task_id,
                timer,
                stepTimer,
                timestamp: Date.now()
            };
            localStorage.setItem('timerState', JSON.stringify(timerState));
        }
    }, [timer, stepTimer, currentTask, isRunning]);

    useEffect(() => {
        let interval = null;
        if (isRunning) {
            interval = setInterval(() => {
                setTimer((time) => time - 1); // Count DOWN
                setStepTimer((time) => time - 1); // Count DOWN for step too
            }, 1000);
        } else if (!isRunning && timer !== 0) {
            clearInterval(interval);
        }
        return () => clearInterval(interval);
    }, [isRunning, timer]);

    const loadWorkingTasks = async () => {
        try {
            const response = await workingTasksAPI.getAll();
            const tasks = response.data.workingTasks;
            setWorkingTasks(tasks);
            
            const active = tasks.find(t => t.is_active);
            if (active) {
                setCurrentTask(active);
                
                // Try to restore timer from localStorage
                const savedState = localStorage.getItem('timerState');
                if (savedState) {
                    const { taskId, timer: savedTimer, stepTimer: savedStepTimer, timestamp } = JSON.parse(savedState);
                    if (taskId === active.task_id) {
                        // Calculate time elapsed since last save
                        const elapsedSeconds = Math.floor((Date.now() - timestamp) / 1000);
                        setTimer(savedTimer - elapsedSeconds);
                        setStepTimer(savedStepTimer - elapsedSeconds);
                        setIsRunning(true);
                        setLoading(false);
                        return;
                    }
                }
                
                // Calculate remaining time (expected duration - time spent)
                const expectedSeconds = (active.expected_duration || 0) * 60;
                const remainingSeconds = expectedSeconds - (active.time_spent || 0);
                setTimer(remainingSeconds);
                
                // Set step timer to countdown from step's expected duration
                const stepExpectedSeconds = (active.current_step_duration || 0) * 60;
                setStepTimer(stepExpectedSeconds);
                
                setIsRunning(true);
            } else if (tasks.length > 0) {
                setCurrentTask(tasks[0]);
                // Set timer to expected duration in seconds
                setTimer((tasks[0].expected_duration || 0) * 60);
                const stepExpectedSeconds = (tasks[0].current_step_duration || 0) * 60;
                setStepTimer(stepExpectedSeconds);
            }
            
            setLoading(false);
        } catch (error) {
            console.error('Error loading working tasks:', error);
            setLoading(false);
        }
    };

    const handleStart = async () => {
        if (!currentTask) return;
        
        try {
            await workingTasksAPI.start(currentTask.task_id);
            setIsRunning(true);
        } catch (error) {
            console.error('Error starting task:', error);
        }
    };

    const handlePause = async () => {
        if (!currentTask) return;
        
        try {
            // Calculate elapsed time (expected duration - remaining time)
            const expectedSeconds = (currentTask.expected_duration || 0) * 60;
            const elapsedTime = expectedSeconds - timer;
            await workingTasksAPI.pause(currentTask.task_id, elapsedTime);
            setIsRunning(false);
        } catch (error) {
            console.error('Error pausing task:', error);
        }
    };

    const handleComplete = async (completeStep = false) => {
        if (!currentTask) return;
        
        try {
            // Calculate elapsed time (expected duration - remaining time)
            const expectedSeconds = (currentTask.expected_duration || 0) * 60;
            const elapsedTime = expectedSeconds - timer;
            
            if (completeStep) {
                // When completing a step, DON'T stop the timer, just move to next step
                await workingTasksAPI.complete(currentTask.task_id, {
                    timeSpent: elapsedTime,
                    completeStep,
                    stepTime: stepTimer
                });
                
                // Reload to get next step, but keep timer running
                await loadWorkingTasks();
            } else {
                // Complete entire task - NOW we stop everything
                await workingTasksAPI.complete(currentTask.task_id, {
                    timeSpent: elapsedTime,
                    completeStep,
                    stepTime: stepTimer
                });
                
                setTimer(0);
                setStepTimer(0);
                setIsRunning(false);
                localStorage.removeItem('timerState');
                await loadWorkingTasks();
            }
        } catch (error) {
            console.error('Error completing task:', error);
        }
    };

    const handleSkip = async () => {
        if (!currentTask) return;
        
        try {
            await workingTasksAPI.skip(currentTask.task_id);
            setTimer(0);
            setStepTimer(0);
            setIsRunning(false);
            localStorage.removeItem('timerState');
            await loadWorkingTasks();
        } catch (error) {
            console.error('Error skipping task:', error);
        }
    };

    const formatTime = (seconds) => {
        const isNegative = seconds < 0;
        const absSeconds = Math.abs(seconds);
        const hrs = Math.floor(absSeconds / 3600);
        const mins = Math.floor((absSeconds % 3600) / 60);
        const secs = absSeconds % 60;
        const timeStr = `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        return isNegative ? `-${timeStr}` : timeStr;
    };

    const getProgressPercentage = () => {
        if (!currentTask) return 0;
        const expectedSeconds = (currentTask.expected_duration || 0) * 60;
        if (expectedSeconds === 0) return 0;
        const elapsed = expectedSeconds - timer;
        return Math.min((elapsed / expectedSeconds) * 100, 100);
    };

    const getTimerColor = () => {
        if (timer < 0) return '#ef4444'; // Red - overtime (nice red)
        if (timer <= 300) return '#f59e0b'; // Yellow/Orange - 5 minutes or less (nice amber)
        return '#10b981'; // Green - more than 5 minutes (nice emerald)
    };

    const getStepTimerColor = () => {
        if (stepTimer < 0) return '#ef4444'; // Red - overtime
        if (stepTimer <= 300) return '#f59e0b'; // Yellow/Orange - 5 minutes or less
        return '#10b981'; // Green - more than 5 minutes
    };

    if (loading) {
        return (
            <>
                <Sidebar />
                <div className="loading-container">
                    <div className="spinner"></div>
                    <p>Loading...</p>
                </div>
            </>
        );
    }

    if (!currentTask) {
        return (
            <>
                <Sidebar />
                <div className="main-content">
                    <div className="focus-container">
                        <div className="empty-state">
                            <h2>📋 No Active Tasks</h2>
                            <p>Add tasks to your working list to start focusing</p>
                            <button onClick={() => navigate('/tasks')} className="btn-primary">
                                Go to All Tasks
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    const expectedMinutes = currentTask.expected_duration || 0;
    const isOvertime = timer < 0;

    return (
        <>
            <Sidebar />
            <div className="main-content">
                <div className="focus-container">
                    <div className="focus-card">
                        <div className="task-header" style={{ borderColor: currentTask.group_color || '#333' }}>
                            <div className="task-group">
                                {currentTask.group_name && (
                                    <span className="group-badge" style={{ backgroundColor: currentTask.group_color }}>
                                        {currentTask.group_name}
                                    </span>
                                )}
                            </div>
                        <h1>{currentTask.task_name}</h1>
                        {currentTask.description && <p className="task-description">{currentTask.description}</p>}
                    </div>

                    <div className="timer-section">
                        <div 
                            className="timer-display"
                            style={{ 
                                color: getTimerColor(),
                                transition: 'color 0.5s ease'
                            }}
                        >
                            {formatTime(timer)}
                        </div>
                        
                        {currentTask.current_step_id && currentTask.current_step_duration && (
                            <div className="step-timer-display">
                                <span className="step-timer-label">Current Step:</span>
                                <span 
                                    className="step-timer-value"
                                    style={{ 
                                        color: getStepTimerColor(),
                                        transition: 'color 0.5s ease'
                                    }}
                                >
                                    {formatTime(stepTimer)}
                                </span>
                                <span className="step-timer-expected">/ {currentTask.current_step_duration} min</span>
                            </div>
                        )}
                        
                        <div className="time-info">
                            <span>Expected: {expectedMinutes} min</span>
                            {isOvertime && <span className="overtime-badge">⚠️ Overtime!</span>}
                        </div>
                        <div className="progress-bar">
                            <div 
                                className="progress-fill" 
                                style={{ 
                                    width: `${getProgressPercentage()}%`,
                                    backgroundColor: isOvertime ? '#e74c3c' : currentTask.group_color || '#3498db'
                                }}
                            ></div>
                        </div>
                    </div>

                    {currentTask.all_steps && currentTask.all_steps.length > 0 && (
                        <div className="steps-section">
                            <h3>Task Steps</h3>
                            <div className="steps-list">
                                {currentTask.all_steps.map((step, index) => (
                                    <div 
                                        key={step.id} 
                                        className={`step-item ${step.is_completed ? 'completed' : ''} ${
                                            step.id === currentTask.current_step_id ? 'active' : ''
                                        }`}
                                    >
                                        <div className="step-number">{index + 1}</div>
                                        <div className="step-content">
                                            <div className="step-name">{step.name}</div>
                                            <div className="step-duration">{step.duration} min</div>
                                        </div>
                                        {step.is_completed && <span className="check-icon">✓</span>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="action-buttons">
                        {!isRunning ? (
                            <button onClick={handleStart} className="btn-primary btn-large">
                                ▶️ Start
                            </button>
                        ) : (
                            <button onClick={handlePause} className="btn-warning btn-large">
                                ⏸️ Pause
                            </button>
                        )}

                        {currentTask.current_step_id && (
                            <button onClick={() => handleComplete(true)} className="btn-info">
                                ✓ Complete Step
                            </button>
                        )}

                        <button onClick={() => handleComplete(false)} className="btn-success">
                            ✓ Complete Task
                        </button>

                        <button onClick={handleSkip} className="btn-secondary">
                            ⏭️ Skip
                        </button>
                    </div>

                    <div className="task-queue">
                        <h3>Task Queue ({workingTasks.length})</h3>
                        <div className="queue-list">
                            {workingTasks.map((task, index) => (
                                <div 
                                    key={task.id} 
                                    className={`queue-item ${task.task_id === currentTask.task_id ? 'current' : ''}`}
                                >
                                    <span className="queue-number">{index + 1}</span>
                                    <span className="queue-name">{task.task_name}</span>
                                    {task.task_id === currentTask.task_id && <span className="current-badge">Current</span>}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="navigation-buttons">
                        <button onClick={() => navigate('/tasks')} className="btn-link">
                            📋 Go to All Tasks
                        </button>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};

export default Focus;
